// Enhanced CompletionStatus Component with improved accessibility
import React, { useEffect, useRef } from 'react';
import { FiCheckCircle, FiUpload } from 'react-icons/fi';

/**
 * Accessible component displayed when file upload is successfully completed
 * - Improved screen reader announcements
 * - Better keyboard focus management
 * - Enhanced semantic structure
 *
 * @param {Object} props - Component props
 * @param {Function} props.onUploadMore - Function to handle uploading more files
 * @returns {JSX.Element} Accessible completion status component
 */
const CompletionStatus = ({ onUploadMore }) => {
  // Ref for announcement and focus management
  const uploadMoreButtonRef = useRef(null);
  const statusRef = useRef(null);

  // Focus the upload more button when component mounts
  useEffect(() => {
    if (uploadMoreButtonRef.current) {
      uploadMoreButtonRef.current.focus();
    }

    // Announce completion to screen readers
    if (statusRef.current) {
      statusRef.current.textContent = 'All files have been successfully uploaded.';
    }
  }, []);

  return (
    <div className="w-100 mb-3">
      {/* Status for screen readers */}
      <div ref={statusRef} className="visually-hidden" role="status" aria-live="polite"></div>

      <div
        className="alert alert-success d-flex align-items-center mb-3 py-3 px-4 fs-6"
        role="alert"
        aria-labelledby="completion-message">
        <FiCheckCircle
          className="me-2 icon-transparent size-medium-large"
          size={22}
          aria-hidden="true"
        />
        <div id="completion-message" className="font-size-medium">
          Upload completed successfully!
        </div>
      </div>

      <button
        ref={uploadMoreButtonRef}
        onClick={onUploadMore}
        className="btn ff-button--primary w-100 py-2 d-flex align-items-center justify-content-center gap-2 font-size-large"
        aria-label="Upload more files">
        <FiUpload size={16} aria-hidden="true" className="icon-transparent size-medium" />
        Upload More Files
      </button>
    </div>
  );
};

export default CompletionStatus;
